import React from 'react';
import { Modal } from 'semantic-ui-react';
import PositionedModal from 'common/components/PositionedModal';
import { StyledButton } from 'common/components/button/StyledButton';
import { Table, TableHeader, TableBody, TableRow, TableCell, HeaderCell } from '../../../common/components/Table';
import StatusLabel from 'common/components/StatusLabel';

interface ReminderModalProps {
    open: boolean;
    name: string;
    email: string;
    onClose: () => void;
    onConfirm: () => void;
}

export const ReminderModal: React.FC<ReminderModalProps> = ({
    open,
    name,
    email,
    onClose,
    onConfirm
}) => (
    <PositionedModal open={open} onClose={onClose} size="small" closeIcon>
        <Modal.Header>Send a reminder to {name}</Modal.Header>

        <Modal.Content style={{ textAlign: 'center' }}>
            <p>
                Are you sure you want to send a survey reminder to <strong>{name}</strong>{email ? ` (${email})` : ''}?
            </p>
        </Modal.Content>

        <Modal.Actions style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <StyledButton label="Cancel" width="120px" color="gray" onClick={onClose} />
            <StyledButton label="Send Reminder" width="160px" color="blue" onClick={onConfirm} />
        </Modal.Actions>
    </PositionedModal>
);

interface EmailLogItem {
    email: string;
    sent_date: string;
    status: string;
    error_message?: string | null;
}

interface EmailHistoryModalProps {
    open: boolean;
    name: string;
    email: string;
    items: EmailLogItem[];
    formatDate: (date: string | Date) => string;
    onClose: () => void;
}

export const EmailHistoryModal: React.FC<EmailHistoryModalProps> = ({
    open,
    name,
    email,
    items,
    formatDate,
    onClose
}) => (
    <PositionedModal open={open} onClose={onClose} size="small" closeIcon>
        <Modal.Header>Message History - {name}</Modal.Header>

        <Modal.Content>
            {items.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#777' }}>No email history available.</p>
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <HeaderCell>Email</HeaderCell>
                            <HeaderCell>Date</HeaderCell>
                            <HeaderCell>Status</HeaderCell>
                            <HeaderCell>Notes</HeaderCell>
                        </TableRow>
                    </TableHeader>

                    <TableBody>
                        {items.map((item, idx) => (
                            <TableRow key={idx}>
                                <TableCell>{item.email}</TableCell>
                                <TableCell>{formatDate(item.sent_date)}</TableCell>
                                <TableCell>
                                    <StatusLabel
                                        color={
                                            item.status === 'sent'
                                                ? '#4caf50'
                                                : item.status === 'failed'
                                                    ? '#f44336'
                                                    : '#9e9e9e'
                                        }
                                    >
                                        {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                    </StatusLabel>
                                </TableCell>
                                <TableCell>{item.error_message || '—'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Modal.Content>

        <Modal.Actions style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <StyledButton label="Close" width="120px" color="gray" onClick={onClose} />
        </Modal.Actions>
    </PositionedModal>
);


interface CancelSurveyModalProps {
    open: boolean;
    name: string;
    email: string;
    isCancelled: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

export const CancelSurveyModal: React.FC<CancelSurveyModalProps> = ({
    open,
    name,
    email,
    isCancelled,
    onClose,
    onConfirm
}) => (
    <PositionedModal open={open} onClose={onClose} size="small" closeIcon>
        <Modal.Header>{isCancelled ? 'Reactivate' : 'Cancel'} Survey</Modal.Header>

        <Modal.Content style={{ textAlign: 'center' }}>
            <p>
                {isCancelled ? (
                    <>
                        Reactivate survey for <strong>{name}</strong>?
                    </>
                ) : (
                    <>
                        Cancel survey invitation for <strong>{name}</strong>?
                    </>
                )}
            </p>
        </Modal.Content>

        <Modal.Actions style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <StyledButton 
                label={isCancelled ? "Keep Cancelled" : "Keep Active"} 
                width="140px" 
                color="gray" 
                onClick={onClose} 
            />
            <StyledButton 
                label={isCancelled ? "Reactivate Survey" : "Cancel Survey"} 
                width="160px" 
                color="blue" 
                onClick={onConfirm} 
            />
        </Modal.Actions>
    </PositionedModal>
);

interface InviteModalProps {
    open: boolean;
    name: string;
    email: string;
    onClose: () => void;
    onConfirm: () => void;
}

export const InviteModal: React.FC<InviteModalProps> = ({
    open,
    name,
    email,
    onClose,
    onConfirm
}) => (
    <PositionedModal open={open} onClose={onClose} size="small" closeIcon>
        <Modal.Header>Invite Member</Modal.Header>

        <Modal.Content style={{ textAlign: 'center' }}>
            <p>
                Create an invitation for <strong>{name}</strong> to participate in the survey?
            </p>
        </Modal.Content>

        <Modal.Actions style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <StyledButton label="Cancel" width="120px" color="gray" onClick={onClose} />
            <StyledButton label="Create Invitation" width="160px" color="blue" onClick={onConfirm} />
        </Modal.Actions>
    </PositionedModal>
);
